import React, { useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { useModalDismiss } from "./useModalDismiss";

interface Category {
    id: string;
    name: string;
}

interface Props {
    categoryId?: string | null;
    categoryName?: string;
    categories?: Category[];
    onSave: (question?: { id: string; title: string; categoryId: string | null }) => void;
    onCancel: () => void;
}

export default function QuestionForm({ categoryId, categoryName, categories, onSave, onCancel }: Props) {
    const fixedCategory = categoryId != null;
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [selectedCat, setSelectedCat] = useState<string>(categoryId || "");
    const [saving, setSaving] = useState(false);
    const createdRef = useRef<{ id: string; title: string; categoryId: string | null } | null>(null);
    const { show, dismiss, onExited } = useModalDismiss(onCancel, () => onSave(createdRef.current || undefined));

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        const t = title.trim();
        if (!t) {
            toast.error("Please enter a question");
            return;
        }
        const cat = fixedCategory ? categoryId : (selectedCat || null);
        setSaving(true);
        axios.post("/api/manuals/questions", {
            title: t,
            body: body.trim(),
            categoryId: cat,
        })
            .then(res => {
                if (res.data.success) {
                    toast.success("Question created");
                    createdRef.current = { id: res.data.item.id, title: res.data.item.title, categoryId: res.data.item.categoryId };
                    dismiss("save");
                } else {
                    toast.error(res.data.message || "Failed to create question");
                    setSaving(false);
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || "Failed to create question");
                setSaving(false);
            });
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered scrollable backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Ask a question</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="m-label">What do you want to know?</label>
                        <input
                            type="text"
                            className="m-input"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            placeholder="e.g. What can I expense?..."
                            maxLength={512}
                            autoFocus
                        />
                    </div>
                    <div className="mb-3">
                        <label className="m-label">Add more details</label>
                        <textarea
                            className="m-input"
                            rows={4}
                            value={body}
                            onChange={e => setBody(e.target.value)}
                            placeholder="Type your text..."
                        />
                    </div>
                    <div className="mb-3">
                        <label className="m-label">Choose a category</label>
                        {fixedCategory ? (
                            <input type="text" className="m-input" value={categoryName || ""} disabled />
                        ) : (
                            <select
                                className="m-input"
                                value={selectedCat}
                                onChange={e => setSelectedCat(e.target.value)}
                            >
                                <option value="">Uncategorized</option>
                                {(categories || []).map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={saving}>
                        {saving ? "Creating..." : "Create question"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
